import { useState, useEffect, useRef } from 'react';
import { Modal, TextField, Checkbox, Text, BlockStack, InlineStack, Select, Spinner, Badge, Button } from '@shopify/polaris';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';
import { AREAS, getAreasSorted, getLineLabel, getLineOptions, getUnitLabel, getUnitOptions } from '../../../../config/roasterySpec';

const MAX_BOM_PREVIEW_ROWS = 14;

function flattenBomTree(nodes, level = 0, acc = []) {
  if (!Array.isArray(nodes)) return acc;

  nodes.forEach((node) => {
    acc.push({
      ...node,
      level
    });

    if (Array.isArray(node.children) && node.children.length > 0) {
      flattenBomTree(node.children, level + 1, acc);
    }
  });

  return acc;
}

function getDisplayPurchasePrice(product) {
  if (!product) return null;
  return product.HasDetails && product.RolledUpPurchasePrice != null ? product.RolledUpPurchasePrice : product.PurchasePrice ?? product.Eenheidsaankoopprijs ?? null;
}

export default function AddProductModal({ open, onClose, onAdd, quotationId, selectedArea, selectedLine, selectedUnit, existingLines = [], replaceLine = null }) {
  const isReplaceMode = !!replaceLine;
  const [productValue, setProductValue] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [unitPurchasePrice, setUnitPurchasePrice] = useState('');
  const [unitSellingPrice, setUnitSellingPrice] = useState('');
  const [isOptional, setIsOptional] = useState(false);
  const [explodeBom, setExplodeBom] = useState(false);
  const [showBomBreakdown, setShowBomBreakdown] = useState(false);
  const [description, setDescription] = useState('');
  const [areaCode, setAreaCode] = useState(selectedArea || 'GRE');
  const [lineNumber, setLineNumber] = useState(selectedLine != null ? String(selectedLine) : '1');
  const [unitType, setUnitType] = useState(selectedUnit != null ? String(Math.floor(selectedUnit / 100) * 100) : '0');
  const [unitInstance, setUnitInstance] = useState(selectedUnit != null ? String(selectedUnit % 100) : '0');
  const [loading, setLoading] = useState(false);
  const [hasBom, setHasBom] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [includeDiscontinued, setIncludeDiscontinued] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProductSummary, setSelectedProductSummary] = useState(null);
  const [bomPreview, setBomPreview] = useState(null);
  const searchRequestRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const bomRequestRef = useRef(0);
  const productDetailsRequestRef = useRef(0);

  // Find next available unit instance for a given area + line + unit type base
  const getNextUnitInstance = (area, line, unitBase) => {
    const base = parseInt(unitBase);
    const usedInstances = existingLines
      .filter((l) => l.AreaCode === area && l.LineNumber === parseInt(line) && Math.floor((l.UnitNumber || 0) / 100) * 100 === base)
      .map((l) => (l.UnitNumber || 0) - base);
    if (usedInstances.length === 0) return '0';
    const max = Math.max(...usedInstances);
    return String(max + 1);
  };

  // Sync area/line/unit selection when modal opens
  useEffect(() => {
    if (open && !replaceLine) {
      const area = selectedArea || 'GRE';
      const line = selectedLine != null ? String(selectedLine) : '1';
      setAreaCode(area);
      setLineNumber(line);
      if (selectedUnit != null) {
        const base = String(Math.floor(selectedUnit / 100) * 100);
        setUnitType(base);
        setUnitInstance(getNextUnitInstance(area, line, base));
      } else {
        setUnitType('0');
        setUnitInstance(getNextUnitInstance(area, line, '0'));
      }
    }
    // Replace mode keeps the line's own quantity/optional flag; the product,
    // description and prices come from whatever the user picks
    if (open && replaceLine) {
      setQuantity(String(replaceLine.Quantity ?? 1));
      setIsOptional(!!replaceLine.IsOptional);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, replaceLine, selectedArea, selectedLine, selectedUnit, existingLines]);

  const resetForm = () => {
    setProductValue(null);
    setQuantity('1');
    setUnitPurchasePrice('');
    setUnitSellingPrice('');
    setIsOptional(false);
    setExplodeBom(false);
    setShowBomBreakdown(false);
    setDescription('');
    setHasBom(false);
    setProductSearch('');
    setSupplierSearch('');
    setIncludeDiscontinued(false);
    setSearchResults([]);
    setSearchLoading(false);
    setHasSearched(false);
    setSelectedProductSummary(null);
    setBomPreview(null);
  };

  const handleProductChange = async (productId, productSummary = null) => {
    setProductValue(productId);
    setSelectedProductSummary(productSummary);

    if (productSummary) {
      const hasBomProduct = !!productSummary.HasDetails;
      const costPrice = getDisplayPurchasePrice(productSummary) || 0;
      setUnitPurchasePrice(String(costPrice));
      setUnitSellingPrice(String(productSummary.SalesPrice ?? 0));
      setDescription(productSummary.ProductNameNL || productSummary.ProductName || '');
      setHasBom(hasBomProduct);
      if (hasBomProduct) {
        setShowBomBreakdown(true);
        setExplodeBom(false);
      } else {
        setShowBomBreakdown(false);
      }
    }

    if (productId) {
      const requestId = ++productDetailsRequestRef.current;
      try {
        const res = await fetch(`/api/products/${productId}`);
        const product = await res.json();
        if (requestId !== productDetailsRequestRef.current) return;
        if (product) {
          const mergedSummary = productSummary ? { ...productSummary, ...product } : product;
          const hasBomProduct = !!product.HasDetails;
          const costPrice = hasBomProduct && product.RolledUpPurchasePrice != null ? product.RolledUpPurchasePrice : product.Eenheidsaankoopprijs || 0;
          setUnitPurchasePrice(String(costPrice));
          setUnitSellingPrice(String(product['Eenheids verkoopprijs'] || 0));
          setDescription(product.ProductNameNL || product.ProductName || '');
          setHasBom(hasBomProduct);
          setSelectedProductSummary((prev) => ({ ...(prev || {}), ...mergedSummary }));
          if (hasBomProduct) {
            setShowBomBreakdown(true);
            setExplodeBom(false);
          } else {
            setShowBomBreakdown(false);
            setBomPreview(null);
          }
        }
      } catch (err) {
        console.log('Error fetching product details:', err);
      }
    } else {
      setUnitPurchasePrice('');
      setUnitSellingPrice('');
      setDescription('');
      setHasBom(false);
      setSelectedProductSummary(null);
      setBomPreview(null);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(async () => {
      const requestId = ++searchRequestRef.current;
      const params = new URLSearchParams();
      if (productSearch.trim()) params.append('q', productSearch.trim());
      if (supplierSearch.trim()) params.append('supplierName', supplierSearch.trim());
      if (includeDiscontinued) params.append('includeDiscontinued', 'true');

      setSearchLoading(true);
      setHasSearched(true);

      try {
        const res = await fetch(`/api/products/advanced-search?${params.toString()}`);
        const data = await res.json();
        if (requestId !== searchRequestRef.current) return;
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (requestId !== searchRequestRef.current) return;
        console.log('Error searching products:', err);
        setSearchResults([]);
      } finally {
        if (requestId === searchRequestRef.current) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [open, productSearch, supplierSearch, includeDiscontinued]);

  useEffect(() => {
    if (!open || !productValue || !hasBom) {
      setBomPreview(null);
      return;
    }

    const requestId = ++bomRequestRef.current;

    async function loadBomPreview() {
      try {
        const res = await fetch(`/api/products/${productValue}/bom/tree`);
        const data = await res.json();
        if (requestId !== bomRequestRef.current) return;
        setBomPreview(data);
      } catch (err) {
        if (requestId !== bomRequestRef.current) return;
        console.log('Error loading BOM preview:', err);
        setBomPreview(null);
      }
    }

    loadBomPreview();
  }, [open, productValue, hasBom]);

  const computedUnitNumber = parseInt(unitType) + parseInt(unitInstance || '0');

  // Duplicate unit check (not relevant when replacing the product on an existing line)
  const isDuplicate = !isReplaceMode && existingLines.some((l) => l.AreaCode === areaCode && l.LineNumber === parseInt(lineNumber) && (l.UnitNumber || 0) === computedUnitNumber);

  // Minimum 54% markup validation
  const ap = parseFloat(unitPurchasePrice) || 0;
  const vp = parseFloat(unitSellingPrice) || 0;
  const minSellingPrice = Math.round(ap * 1.54 * 100) / 100;
  const markupTooLow = ap > 0 && vp < minSellingPrice;
  const applyMinimumSellingPrice = () => setUnitSellingPrice(minSellingPrice.toFixed(2));

  const handleAdd = async () => {
    setLoading(true);

    try {
      if (isReplaceMode) {
        // Overwrite the existing line with the chosen product
        const res = await fetch(`/api/project-quotations/${quotationId}/lines/${replaceLine.ID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ProductID: productValue || null,
            Description: description || null,
            Quantity: parseFloat(quantity) || 1,
            UnitPurchasePrice: parseFloat(unitPurchasePrice) || 0,
            UnitSellingPrice: parseFloat(unitSellingPrice) || 0,
            IsOptional: isOptional,
            ShowBomBreakdown: showBomBreakdown && hasBom
          })
        });
        const data = await res.json();
        if (res.ok) {
          onAdd(data);
          resetForm();
          onClose();
        }
      } else {
        const payload = {
          AreaCode: areaCode,
          LineNumber: parseInt(lineNumber),
          UnitNumber: computedUnitNumber,
          ProductID: productValue || null,
          Description: description || null,
          Quantity: parseFloat(quantity) || 1,
          UnitPurchasePrice: parseFloat(unitPurchasePrice) || 0,
          UnitSellingPrice: parseFloat(unitSellingPrice) || 0,
          IsOptional: isOptional,
          ShowBomBreakdown: showBomBreakdown && hasBom
        };
        const endpoint = explodeBom && hasBom ? `/api/project-quotations/${quotationId}/lines/add-with-bom` : `/api/project-quotations/${quotationId}/lines`;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok) {
          onAdd(data);
          resetForm();
          onClose();
        }
      }
    } catch (err) {
      console.log('Error saving line:', err);
    }
    setLoading(false);
  };

  const areaOptions = getAreasSorted().map((a) => ({ label: a.label, value: a.code }));
  const lineOptions = getLineOptions();
  const unitOptions = getUnitOptions();
  const flattenedBomRows = flattenBomTree(bomPreview?.tree || []);
  const bomPreviewRows = flattenedBomRows.slice(0, MAX_BOM_PREVIEW_ROWS);
  const bomPreviewHiddenCount = Math.max(flattenedBomRows.length - MAX_BOM_PREVIEW_ROWS, 0);
  const suppressEnterSubmit = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const contextLabel = `${AREAS[areaCode]?.label || areaCode} > ${String(lineNumber).padStart(3, '0')} - ${getLineLabel(lineNumber)} > ${String(computedUnitNumber).padStart(4, '0')} - ${getUnitLabel(
    computedUnitNumber
  )}`;

  return (
    <>
      {open && (
        <style>{`
          .Polaris-Modal-Dialog__Modal {
            width: min(1520px, 96vw) !important;
            max-width: 1520px !important;
          }
        `}</style>
      )}
      <Modal
        open={open}
        large
        onClose={() => {
          resetForm();
          onClose();
        }}
        title={isReplaceMode ? 'Replace Product on Line' : 'Add Product to Quotation'}
        primaryAction={{
          content: isReplaceMode ? 'Replace' : 'Add',
          onAction: handleAdd,
          loading,
          disabled: isReplaceMode ? !productValue || markupTooLow : !lineNumber || markupTooLow || isDuplicate
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              resetForm();
              onClose();
            }
          }
        ]}
      >
        <Modal.Section>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (loading || markupTooLow) return;
              if (isReplaceMode ? productValue : !isDuplicate && lineNumber) handleAdd();
            }}
          >
            <button type="submit" style={{ display: 'none' }} />
            <BlockStack gap="400">
              {isReplaceMode ? (
                <Text variant="bodySm" as="p" tone="subdued">
                  Replacing product on: <strong>{`${replaceLine.Product?.Productref || replaceLine.ProductID || 'no product'} — ${replaceLine.Description || 'Unnamed line'}`}</strong> (
                  {`${replaceLine.AreaCode} > ${String(replaceLine.LineNumber).padStart(3, '0')} > ${String(replaceLine.UnitNumber || 0).padStart(4, '0')}`})
                </Text>
              ) : (
                <Text variant="bodySm" as="p" tone="subdued">
                  Adding to: <strong>{contextLabel}</strong>
                </Text>
              )}

              {!isReplaceMode && (
                <>
                  <InlineStack gap="300">
                    <div style={{ flex: 1 }}>
                      <Select label="Area" options={areaOptions} value={areaCode} onChange={setAreaCode} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Select label="Line" options={lineOptions} value={lineNumber} onChange={setLineNumber} />
                    </div>
                  </InlineStack>

                  <InlineStack gap="300">
                    <div style={{ flex: 1 }}>
                      <Select
                        label="Unit Type"
                        options={unitOptions}
                        value={unitType}
                        onChange={(v) => {
                          setUnitType(v);
                          setUnitInstance(getNextUnitInstance(areaCode, lineNumber, v));
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Unit Instance"
                        type="number"
                        value={unitInstance}
                        onChange={setUnitInstance}
                        autoComplete="off"
                        min={0}
                        max={99}
                        helpText={`Full unit: ${String(computedUnitNumber).padStart(4, '0')}`}
                        error={isDuplicate ? 'This unit already exists' : undefined}
                      />
                    </div>
                  </InlineStack>
                </>
              )}

              <div
                style={{
                  border: '1px solid #e1e3e5',
                  borderRadius: '12px',
                  padding: '16px',
                  background: '#fafbfb'
                }}
              >
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <div>
                      <Text variant="headingSm" as="h3">
                        Product Search
                      </Text>
                      <Text variant="bodySm" as="p" tone="subdued">
                        Search by code, ref, or product name. Filter by supplier for faster quoting.
                      </Text>
                    </div>
                    <InlineStack gap="200" blockAlign="center">
                      {selectedProductSummary && <Badge tone="success">Selected #{selectedProductSummary.ProductID}</Badge>}
                      <Text variant="bodySm" as="span" tone="subdued">
                        {searchLoading ? 'Searching...' : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'}`}
                      </Text>
                    </InlineStack>
                  </InlineStack>

                  <InlineStack gap="300" wrap={false}>
                    <div style={{ flex: 1.8 }}>
                      <TextField label="Quick search" value={productSearch} onChange={setProductSearch} onKeyDown={suppressEnterSubmit} autoComplete="off" placeholder="Product ID, ref, or name" />
                    </div>
                    <div style={{ flex: 1.2 }}>
                      <TextField label="Supplier" value={supplierSearch} onChange={setSupplierSearch} onKeyDown={suppressEnterSubmit} autoComplete="off" placeholder="Filter by supplier name" />
                    </div>
                    <div style={{ paddingTop: '28px' }}>
                      <Checkbox label="Include discontinued" checked={includeDiscontinued} onChange={setIncludeDiscontinued} />
                    </div>
                    <div style={{ paddingTop: '28px' }}>
                      <Button
                        onClick={() => {
                          setProductSearch('');
                          setSupplierSearch('');
                          setIncludeDiscontinued(false);
                        }}
                      >
                        Clear filters
                      </Button>
                    </div>
                  </InlineStack>

                  <div
                    style={{
                      border: '1px solid #dfe3e8',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      background: '#fff'
                    }}
                  >
                    <div style={{ maxHeight: '340px', overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f6f6f7' }}>
                          <tr>
                            {['Select', 'Code', 'Ref', 'Product', 'Supplier', 'AP', 'VP', 'Type', 'Status'].map((heading) => (
                              <th
                                key={heading}
                                style={{
                                  padding: '10px 12px',
                                  textAlign: heading === 'AP' || heading === 'VP' ? 'right' : 'left',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#616161',
                                  borderBottom: '1px solid #dfe3e8'
                                }}
                              >
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {searchLoading ? (
                            <tr>
                              <td colSpan={9} style={{ padding: '32px 16px', textAlign: 'center' }}>
                                <InlineStack align="center" gap="200">
                                  <Spinner size="small" />
                                  <Text as="span" variant="bodySm" tone="subdued">
                                    Searching products...
                                  </Text>
                                </InlineStack>
                              </td>
                            </tr>
                          ) : searchResults.length > 0 ? (
                            searchResults.map((product) => {
                              const isSelected = Number(productValue) === Number(product.ProductID);
                              const purchasePrice = getDisplayPurchasePrice(product);
                              return (
                                <tr
                                  key={product.ProductID}
                                  onClick={() => handleProductChange(product.ProductID, product)}
                                  onDoubleClick={() => handleProductChange(product.ProductID, product)}
                                  style={{
                                    cursor: 'pointer',
                                    background: isSelected ? '#edf7ff' : 'transparent'
                                  }}
                                >
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>
                                    <Button
                                      size="slim"
                                      variant={isSelected ? 'primary' : undefined}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleProductChange(product.ProductID, product);
                                      }}
                                    >
                                      {isSelected ? 'Selected' : 'Select'}
                                    </Button>
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top', fontWeight: 600 }}>{product.ProductID}</td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>{product.Productref || '-'}</td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>
                                    <div style={{ fontWeight: 500 }}>{product.ProductNameNL || product.ProductName || '-'}</div>
                                    {product.Omschrijving && (
                                      <div
                                        style={{
                                          marginTop: '4px',
                                          color: '#6d7175',
                                          fontSize: '12px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {product.Omschrijving}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>{product.SupplierName || '-'}</td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top', textAlign: 'right' }}>
                                    {purchasePrice != null ? formatToCurrency(purchasePrice) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top', textAlign: 'right' }}>
                                    {product.SalesPrice != null ? formatToCurrency(product.SalesPrice) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>
                                    {product.HasDetails ? <Badge tone="info">BOM</Badge> : <Badge>Single</Badge>}
                                  </td>
                                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f2f3', verticalAlign: 'top' }}>
                                    {product.Discontinued ? <Badge tone="warning">Discontinued</Badge> : <Badge tone="success">Active</Badge>}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={9} style={{ padding: '28px 16px', textAlign: 'center' }}>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {hasSearched ? 'No products found. Try a broader search or another supplier.' : 'Start typing to search products.'}
                                </Text>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {selectedProductSummary && (
                    <InlineStack gap="300" wrap={false}>
                      <div
                        style={{
                          flex: 1.15,
                          border: '1px solid #dfe3e8',
                          borderRadius: '10px',
                          background: '#fff',
                          padding: '14px 16px'
                        }}
                      >
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingSm" as="h4">
                              Selected Product
                            </Text>
                            <InlineStack gap="200">
                              {selectedProductSummary.HasDetails ? <Badge tone="info">BOM</Badge> : <Badge>Single item</Badge>}
                              {selectedProductSummary.Discontinued && <Badge tone="warning">Discontinued</Badge>}
                            </InlineStack>
                          </InlineStack>

                          <div>
                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                              #{selectedProductSummary.ProductID} - {selectedProductSummary.ProductNameNL || selectedProductSummary.ProductName || '-'}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Ref: {selectedProductSummary.Productref || '-'} · Supplier: {selectedProductSummary.SupplierName || '-'}
                            </Text>
                          </div>

                          <InlineStack gap="400">
                            <div>
                              <Text as="p" variant="bodySm" tone="subdued">
                                Purchase price
                              </Text>
                              <Text as="p" variant="bodyMd" fontWeight="semibold">
                                {getDisplayPurchasePrice(selectedProductSummary) != null ? formatToCurrency(getDisplayPurchasePrice(selectedProductSummary)) : '-'}
                              </Text>
                            </div>
                            <div>
                              <Text as="p" variant="bodySm" tone="subdued">
                                Selling price
                              </Text>
                              <Text as="p" variant="bodyMd" fontWeight="semibold">
                                {selectedProductSummary.SalesPrice != null ? formatToCurrency(selectedProductSummary.SalesPrice) : '-'}
                              </Text>
                            </div>
                            <div>
                              <Text as="p" variant="bodySm" tone="subdued">
                                Price source
                              </Text>
                              <Text as="p" variant="bodyMd" fontWeight="semibold">
                                {selectedProductSummary.HasDetails ? 'BOM roll-up' : 'Direct AP'}
                              </Text>
                            </div>
                          </InlineStack>
                        </BlockStack>
                      </div>

                      <div
                        style={{
                          flex: 1,
                          border: '1px solid #dfe3e8',
                          borderRadius: '10px',
                          background: '#fff',
                          padding: '14px 16px'
                        }}
                      >
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingSm" as="h4">
                              BOM Preview
                            </Text>
                            {hasBom && bomPreview?.leafCount != null && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {bomPreview.leafCount} leaf part{bomPreview.leafCount === 1 ? '' : 's'}
                              </Text>
                            )}
                          </InlineStack>

                          {!hasBom ? (
                            <Text as="p" variant="bodySm" tone="subdued">
                              This product is a direct line item without a bill of materials.
                            </Text>
                          ) : bomPreview ? (
                            <>
                              <InlineStack gap="400">
                                <div>
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    Rolled-up cost
                                  </Text>
                                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                                    {formatToCurrency(bomPreview.rolledUpCost || 0)}
                                  </Text>
                                </div>
                                <div>
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    Visible parts
                                  </Text>
                                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                                    {flattenedBomRows.length}
                                  </Text>
                                </div>
                              </InlineStack>

                              <div
                                style={{
                                  maxHeight: '180px',
                                  overflow: 'auto',
                                  borderTop: '1px solid #f1f2f3',
                                  paddingTop: '10px'
                                }}
                              >
                                {bomPreviewRows.map((item, index) => (
                                  <div
                                    key={`${item.productId}-${index}`}
                                    style={{
                                      padding: '6px 0 6px 0',
                                      paddingLeft: `${item.level * 18}px`,
                                      borderBottom: index === bomPreviewRows.length - 1 ? 'none' : '1px solid #f6f6f7'
                                    }}
                                  >
                                    <Text as="p" variant="bodySm">
                                      <strong>{item.quantity}x</strong> {item.ref || '-'} - {item.name || '-'}
                                    </Text>
                                  </div>
                                ))}
                                {bomPreviewHiddenCount > 0 && (
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    +{bomPreviewHiddenCount} more BOM item{bomPreviewHiddenCount === 1 ? '' : 's'}
                                  </Text>
                                )}
                              </div>
                            </>
                          ) : (
                            <InlineStack gap="200" blockAlign="center">
                              <Spinner size="small" />
                              <Text as="span" variant="bodySm" tone="subdued">
                                Loading BOM preview...
                              </Text>
                            </InlineStack>
                          )}
                        </BlockStack>
                      </div>
                    </InlineStack>
                  )}
                </BlockStack>
              </div>

              <TextField label="Description (override)" value={description} onChange={setDescription} autoComplete="off" />

              <InlineStack gap="300">
                <div style={{ flex: 1 }}>
                  <TextField label="Quantity" type="number" value={quantity} onChange={setQuantity} autoComplete="off" min={0} step={0.01} />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField label="Purchase price (AP)" type="number" value={unitPurchasePrice} onChange={setUnitPurchasePrice} autoComplete="off" prefix="€" step={0.01} />
                </div>
                <div style={{ flex: 1 }}>
                  <BlockStack gap="200">
                    <TextField
                      label="Selling price (VP)"
                      type="number"
                      value={unitSellingPrice}
                      onChange={setUnitSellingPrice}
                      autoComplete="off"
                      prefix="€"
                      step={0.01}
                      error={markupTooLow ? `Min. €${minSellingPrice.toFixed(2)} (54% markup)` : undefined}
                    />
                    {markupTooLow && (
                      <div>
                        <Button size="slim" onClick={applyMinimumSellingPrice}>
                          Set VP to minimum (€{minSellingPrice.toFixed(2)})
                        </Button>
                      </div>
                    )}
                  </BlockStack>
                </div>
              </InlineStack>

              <InlineStack gap="400">
                <Checkbox label="Optional item" checked={isOptional} onChange={setIsOptional} />
                {hasBom && (
                  <Checkbox
                    label="Show BOM breakdown (read-only)"
                    checked={showBomBreakdown}
                    onChange={(v) => {
                      setShowBomBreakdown(v);
                      if (v) setExplodeBom(false);
                    }}
                  />
                )}
                {hasBom && !isReplaceMode && (
                  <Checkbox
                    label="Explode BOM into separate lines"
                    checked={explodeBom}
                    onChange={(v) => {
                      setExplodeBom(v);
                      if (v) setShowBomBreakdown(false);
                    }}
                  />
                )}
              </InlineStack>
            </BlockStack>
          </form>
        </Modal.Section>
      </Modal>
    </>
  );
}
